import { DestroyRef, Injectable, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

import { Store } from '@ngrx/store'

import { Observable, Subject, of, switchMap, tap } from 'rxjs'
import { distinctUntilChanged, filter, map, take } from 'rxjs/operators'

import { ApplicationApi } from '@flowx/designer/shared/api/application.api'
import { ApplicationResourceType } from '@flowx/designer/shared/entities/application.entity'
import { ToastMessageService } from '@flowx/designer/shared/services/toast-message.service'
import {
  selectAllRouteParams,
  selectSegmentsAndParams,
} from '@flowx/designer/shared/store/selectors/router.selectors'

const APPLICATION_ID_INDEX = 4
const CURRENT_APPLICATION_VERSION_ID_INDEX = 6
const RESOURCE_TYPE_INDEX = 7
const COMPOSITE_RESOURCE_TYPE_INDEX = 8
const ENUMERATIONS_RESOURCE_TYPE = 'enumerations'

@Injectable()
export class RoutePersistenceService {
  #ngrxStore = inject(Store)
  #toastMessageService = inject(ToastMessageService)
  #applicationApi = inject(ApplicationApi)
  #destroyRef = inject(DestroyRef)

  refreshResources$ = new Subject<void>()

  onlyListResources = [
    'data-model',
    'substitution-tags',
    'media-library',
    'document-intelligence',
    'stages',
    'allocations',
    'dependencies',
    'configurationParameters',
    'settings',
  ]

  compositeResources = ['reusable-resources', 'integration-designer']

  possibleSubResources = [
    'endpoints',
    'operations',
    'sql-operations',
    'kb-stores',
    'file-operations',
    'soap-operations',
  ]

  routeResourceTypeToApplicationResourceTypeMapper = {
    'processes': ApplicationResourceType.Process,
    'notification-templates': ApplicationResourceType.NotificationTemplate,
    'document-templates': ApplicationResourceType.DocumentTemplate,
    'views': ApplicationResourceType.View,
    'ui-templates': ApplicationResourceType.ReusableTemplate,
    'functions': ApplicationResourceType.ReusableFunction,
    'custom-components': ApplicationResourceType.CustomComponent,
    'data-sources': ApplicationResourceType.System,
    'endpoints': ApplicationResourceType.System,
    'operations': ApplicationResourceType.System,
    'sql-operations': ApplicationResourceType.System,
    'kb-stores': ApplicationResourceType.System,
    'soap-operations': ApplicationResourceType.System,
    'workflows': ApplicationResourceType.Workflow,
    'enumerations': ApplicationResourceType.Enumeration,
    'proxy-connections': ApplicationResourceType.ProxyConnection,
    'ui-flows': ApplicationResourceType.UiFlow,
  }

  constructor() {
    this.refreshListListener()
  }

  refreshListListener(): void {
    this.#ngrxStore
      .select(selectAllRouteParams)
      .pipe(
        filter((params) => !!params.appVersionId && !!params.appId),
        map((params) => ({
          appId: params.appId,
          appVersionId: params.appVersionId,
        })),
        distinctUntilChanged((prev, curr) => prev.appVersionId === curr.appVersionId),
        tap(() => this.refreshResources$.next()),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe()
  }

  getUpdatedRoute(
    nextApplicationVersionId: string
  ): Observable<{ fragments: string[]; queryParams: any }> {
    return this.#ngrxStore.select(selectSegmentsAndParams).pipe(
      map((data) => ({
        paths: data.segments.map((segment) => segment.path),
        queryParams: data.queryParams,
      })),
      take(1),
      switchMap((data) => {
        const { paths, queryParams } = data
        const resourceType = this.getResourceType(paths)

        if (!this.onlyListResources.includes(resourceType)) {
          const currentResourceId = this.getResourceId(resourceType, paths, queryParams)

          if (currentResourceId) {
            const params = {
              'source-app-version-id': paths[CURRENT_APPLICATION_VERSION_ID_INDEX],
              'source-ri': currentResourceId,
            }

            return this.resourceExistsInNextVersion(
              paths[APPLICATION_ID_INDEX],
              nextApplicationVersionId,
              this.routeResourceTypeToApplicationResourceTypeMapper[resourceType],
              params
            ).pipe(
              tap((data) => {
                if (!data) {
                  this.#toastMessageService.showWarningMessage(
                    'Resource does not exist in current version'
                  )
                }
              }),
              map((data) => {
                let fragments = this.getFragmentsWithNextApplicationVersionId(
                  paths,
                  nextApplicationVersionId
                )

                if (data) {
                  const { resourceId: nextResourceId, resourceName } = data

                  if (resourceType !== ENUMERATIONS_RESOURCE_TYPE) {
                    fragments = fragments.map((fragment) =>
                      fragment === currentResourceId ? nextResourceId : fragment
                    )

                    fragments = this.treatSpecialRoutingCases(
                      fragments,
                      resourceType,
                      nextResourceId
                    )
                  } else {
                    return {
                      fragments,
                      queryParams: {
                        path: JSON.stringify([{ name: resourceName, id: nextResourceId }]),
                      },
                    }
                  }
                } else {
                  fragments = fragments.slice(0, fragments.indexOf(currentResourceId))

                  if (this.possibleSubResources.includes(resourceType)) {
                    fragments = fragments.slice(0, -1).concat('data-sources')
                  }
                }

                return { fragments, queryParams: null }
              })
            )
          }
        }

        return of({
          fragments: this.getFragmentsWithNextApplicationVersionId(paths, nextApplicationVersionId),
          queryParams: null,
        })
      })
    )
  }

  getResourceType(paths: string[]): string {
    const resourceType = paths[RESOURCE_TYPE_INDEX]

    if (!this.compositeResources.includes(resourceType)) {
      return resourceType
    } else {
      return paths[COMPOSITE_RESOURCE_TYPE_INDEX]
    }
  }

  getResourceId(resourceType: string, paths: string[], queryParams: any): string {
    if (resourceType !== ENUMERATIONS_RESOURCE_TYPE) {
      return paths[paths.indexOf(resourceType) + 1]
    } else if (resourceType === ENUMERATIONS_RESOURCE_TYPE && queryParams.path) {
      const parsedQueryParams = JSON.parse(queryParams.path)
      return parsedQueryParams[0].id
    }

    return ''
  }

  resourceExistsInNextVersion(
    applicationId: string,
    nextApplicationVersionId: string,
    resourceType: ApplicationResourceType,
    params: object
  ): Observable<{ resourceId: string; resourceName: string }> {
    return this.#applicationApi
      .resourceExistsInNextVersion(applicationId, nextApplicationVersionId, resourceType, params)
      .pipe(
        map((data) =>
          data ? { resourceId: data.resourceId, resourceName: data.resourceName } : null
        )
      )
  }

  getFragmentsWithNextApplicationVersionId(
    paths: string[],
    nextApplicationVersionId: string
  ): string[] {
    return paths.map((value, index) =>
      index === CURRENT_APPLICATION_VERSION_ID_INDEX ? nextApplicationVersionId : value
    )
  }

  treatSpecialRoutingCases(
    fragments: string[],
    resourceType: string,
    nextResourceId: string
  ): string[] {
    if (resourceType === 'processes' && fragments.at(-1) === 'ui') {
      // We don't check the data in the query params, like selectedSwimlaneId, so routing to ui will result in a broken state.
      return fragments.slice(0, -2).concat('diagram')
    }

    if (this.possibleSubResources.includes(resourceType)) {
      // We don't handle sub-resources yet.
      return fragments.slice(0, -3).concat('data-sources', nextResourceId)
    }

    return fragments
  }
}
